import React, { useEffect, useState, useRef } from 'react';
import { GetReportTimeline } from 'src/request/Reports';
import useCustomPagination from 'src/utils/useCustomPagination';
import {
  FaFileImage,
  FaFileExcel,
  FaPhotoVideo,
  FaFilePdf,
  FaFile,
} from 'react-icons/fa';
import { SkeletonCard } from '../dashboard/component/SkeletonCards';
const Timeline = ({ project_id }) => {
  const [timelineData, setTimelineData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [attachmentType, setAttachmentType] = useState(null);
  const scrollContainerRef = useRef(null);
  const { currentPage, pageSize, totalPages, updatePagination } = useCustomPagination();
  const attachmentIcons = {
    image: { component: FaFileImage, color: '#0052CC', typeId: 1 },
    pdf: { component: FaFilePdf, color: '#D32F2F', typeId: 2 },
    excel: { component: FaFileExcel, color: '#107c41', typeId: 3 },
    video: { component: FaPhotoVideo, color: '#0052CC', typeId: 4 },
  };

  useEffect(() => {
    if (project_id) {
      fetchReportTimeline();
    }
  }, [project_id]);

  useEffect(() => {
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container || isFetching) return;

      const threshold = 50;
      const atBottom =
        container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;

      if (atBottom) {
        if (currentPage < totalPages) {
          fetchReportTimeline(true);
        }
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [currentPage, totalPages, isFetching, project_id]);


  const fetchReportTimeline = async (isScrolling) => {
    if (isFetching) return;
    if (totalPages && currentPage > totalPages) return;

    setIsFetching(true);
    if (isScrolling) {
      updatePagination({ newCurrentPage: currentPage + 1 });
    }

    const newCurrentPage = isScrolling ? currentPage + 1 : currentPage;
    try {

      const result = await GetReportTimeline({ page: newCurrentPage, per_page: pageSize, project_id });
      if (result.status === 200) {
        const newData = result?.data?.data || [];
        setTimelineData(prev => {
          const existingIds = new Set(prev.map(item => item.comment_id));
          const filteredNew = newData.filter(item => !existingIds.has(item.comment_id));
          return [...prev, ...filteredNew];
        });

        const totalPages_ = result?.data?.total_pages ?? 0;
        if (totalPages !== totalPages_) {
          updatePagination({ newTotalPages: totalPages_ });
        }
      }
    }
    finally {
      setIsFetching(false);
    }


  };

  const groupByDate = (data) => {
    const grouped = {};
    data.forEach((entry) => {
      if (!entry.attachments?.some(att => !!att.url)) return;

      const date = new Date(entry.date_created).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(entry);
    });
    return grouped;
  };
  const handleAttachmentView = (type_name, url) => {
    const type = type_name.toLowerCase();
    const typeId = attachmentIcons[type]?.typeId;

    if ([1, 4].includes(typeId)) {
      setAttachmentType(typeId);
      setSelectedImage(url);
      setShowModal(true);
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = decodeURIComponent(url.split('/').pop()) || 'file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const MediaModal = ({ show, imageUrl, attachmentType, onClose }) => {
    const [loading, setLoading] = useState(true);
    if (!show || !imageUrl) return null;

    return (
      <>
        <div className="modal show d-flex align-items-center justify-content-center position-fixed w-100 h-100" tabIndex="-1" role="dialog" style={{ zIndex: 1050, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div
            className="modal-dialog d-flex align-items-center justify-content-center"
            role="document"
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
            }}
          >
            <div className="bg-transparent border-0 modal-content d-flex align-items-center justify-content-center position-relative">
              <button
                type="button"
                className="btn btn-outline-light rounded-circle"
                style={{
                  width: '40px',
                  height: '40px',
                  fontSize: '1.5rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  border: 'none',
                  color: 'white',
                  backdropFilter: 'blur(8px)',
                  position: 'absolute',
                  top: '10px',
                  right: '15px',
                  zIndex: 1060,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: '0.3s',
                  cursor: 'pointer',
                }}
                onClick={onClose}
                aria-label="Close"
              >
                ✕
              </button>

              <div
                className="modal-body d-flex align-items-center justify-content-center"
                style={{
                  width: '100vw',
                  height: '100vh',
                }}
              >
                {attachmentType === 4 ? (
                  <video
                    controls
                    className="img-fluid"
                    style={{
                      borderRadius: '12px',
                      maxHeight: '85vh',
                      maxWidth: '80vw',
                      objectFit: 'contain',
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <source src={imageUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <>
                    {loading && (
                      <div
                        className="text-white spinner-border"
                        style={{
                          width: '120px',
                          height: '120px',
                          position: 'absolute',
                        }}
                        role="status"
                      ></div>
                    )}

                    <img
                      src={imageUrl}
                      alt="Attachment Preview"
                      className="img-fluid"
                      style={{
                        borderRadius: '12px',
                        maxHeight: '85vh',
                        maxWidth: '80vw',
                        objectFit: 'contain',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                        visibility: loading ? 'hidden' : 'visible',
                        opacity: loading ? 0 : 1,
                        transition: 'opacity 0.3s ease-in-out',
                      }}
                      onLoad={() => setLoading(false)}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop show position-fixed w-100 h-100" style={{ zIndex: 1040, backgroundColor: 'rgba(0, 0, 0, 0.5)' }} onClick={onClose}></div>
      </>
    );
  };

  const FilePreview = ({ item }) => {
    const type = item.type_name.toLowerCase();
    const iconData = attachmentIcons[type] || { component: FaFile, color: '#888888' };
    const Icon = iconData.component;

    return (
      <div
        className="hover:opacity-70 d-flex flex-column align-items-center"
        style={{
          width: '100px',
          height: '80px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#ffffff',
          borderRadius: '5px',
          border: '1px solid #dfe1e6',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Icon
          size={40}
          style={{ color: iconData.color, cursor: 'pointer' }}
          onClick={() => handleAttachmentView(type, item.url)}
        />
      </div>
    );
  };

  return (
    <>
      <div className='my-4'>
        <p className="mb-1 text-2xl font-bold tracking-wide text-left ">
          Upload History
        </p>
        <p className='mt-0 text-sm text-gray-500'>
          A reverse chronological timeline of all report file uploads
        </p>
      </div>
      <div className="max-w-7xl p-5 mx-auto max-h-[550px] overflow-auto border mb-2" ref={scrollContainerRef}>
        {timelineData.length > 0 ? (
          Object.entries(groupByDate(timelineData)).map(([date, entries]) => (
            <div key={date} className="mb-5">
              <h6 className="mb-3 fw-bold text-aimhi-textlight-neutral">{date}</h6>
              <ol className="relative border-gray-200 border-s">
                {entries.map((entry) => (
                  <li key={`comment-${entry.comment_id}`} className="mb-10 ms-4">
                    <div className="absolute w-3 h-3 bg-aimhi-textlight-neutral rounded-full mt-1.5 -start-1.5 border border-white"></div>
                    <p className="mb-1 text-sm font-medium text-gray-800">{entry.full_name}</p>
                    <p className="mb-1 text-sm text-gray-800">{entry.scope_of_work} - {entry.work_item}</p>
                    <time className="block mb-2 text-sm text-muted">{entry.date_created}</time>

                    <p className="text-sm text-gray-600">{entry.message}</p>

                    {entry.attachments && entry.attachments.length > 0 && (
                      <div className="flex-wrap gap-2 mt-2 border-gray-300 d-flex">
                        {entry.attachments.map((att, i) => (
                          <div
                            key={`${entry.comment_id || 'noid'}-att-${att.id || 'noatt'}-${i}`}
                            className="d-flex flex-column align-items-center"
                            style={{
                              minWidth: '100px',
                              maxWidth: '110px',
                              textAlign: 'center',
                              padding: '8px',
                              borderRadius: '5px',
                              backgroundColor: '#f4f5f7',
                              border: '1px solid #dfe1e6',
                            }}
                          >
                            <FilePreview item={att} />
                            <span
                              style={{
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                fontSize: '11px',
                                color: '#42526e',
                                marginTop: '5px',
                                maxWidth: '90px',
                              }}
                              title={att.file_name}
                            >
                              {att.file_name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          ))
        ) : (
          <>
            <SkeletonCard />
          </>
        )}
      </div>
      <MediaModal
        show={showModal}
        imageUrl={selectedImage}
        attachmentType={attachmentType}
        onClose={() => setShowModal(false)}
      />
    </>
  );
};

export default Timeline;
